// EditTest.js — Phase 3 rewrite, Sub-slice 3 (final).
//
// Layout: a single 3-column table — Field | Current | Editing — with
// the disk editor (paired CURRENT/EDITING rows) and plate image below.
//
// Two paths to reach this page:
//   Path 1 — User-initiated correction request from ViewTest. The
//            correction request shows as a red Alert at top with the
//            requester, timestamp, and their reason text. Save resolves
//            the correction request.
//   Path 2 — Direct admin edit (no prior correction request). No Alert
//            at top. Save just appends the new revision.
//
// Editor must enter a "Reason for this edit" before saving (mandatory
// audit trail). Field is inline at the bottom of the form, validated
// alongside other required fields.
//
// Flow:
//   Edit fields → Click Preview → Modal shows diff + reason →
//   Click "Save revision" → Calls Cloud Function `interpret_measurements`
//   to re-derive interpretations → Calls firestoreService.addRevision
//   to atomically write revision N+1 → Resolves any open
//   correctionRequest → Toast notification → Navigate per role.
//
// Cascade resets are aggressive — changing a parent field blanks all
// dependent children to force re-confirmation.
//   - Organism change → host species AND clinical indication cleared
//   - Host species change → clinical indication cleared
//   - Antibiotic change (per disk) → potency, zone, mode cleared

import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { app } from "./firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getCurrentRevision,
  getOrganizationDetails,
  getSpecimenTypes,
  getClinicalIndications,
  getAmrieAntibiotics,
  getAmrieOrganisms,
  getSpeciesMaster,
  getAmrieBreakpoints,
  getCustomBreakpoints,
  getOpenRequestForTest,
  getUserProfile,
  addRevision,
  resolveCorrectionRequest,
  getTestResultById,
} from "./firestoreService";
import DiskAssessmentEditor, { diskRowComplete } from "./DiskAssessmentEditor";
import { amrieHostsToTry, filterClinicalIndications } from "./editCascade";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";

// Background tints — match DiskAssessmentEditor for visual consistency.
const CURRENT_BG = "rgba(0, 0, 0, 0.025)";
const CHANGED_BG = "rgba(255, 167, 38, 0.08)";
const FIELD_COL_BG = "grey.100";

const COL_WIDTH_FIELD = "22%";
const COL_WIDTH_CURRENT = "33%";
const COL_WIDTH_EDITING = "45%";

/**
 * One row of the Field | Current | Editing table. Pure layout — the
 * caller passes in the rendered current value and the rendered editing
 * control.
 */
const FieldRow = ({ label, currentDisplay, editingControl, changed }) => (
  <TableRow>
    <TableCell
      sx={{
        bgcolor: FIELD_COL_BG,
        fontWeight: "bold",
        verticalAlign: "middle",
        width: COL_WIDTH_FIELD,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      sx={{
        bgcolor: CURRENT_BG,
        verticalAlign: "middle",
        color: "text.secondary",
        width: COL_WIDTH_CURRENT,
      }}
    >
      {currentDisplay || (
        <Typography component="span" sx={{ color: "text.disabled" }}>
          —
        </Typography>
      )}
    </TableCell>
    <TableCell
      sx={{
        bgcolor: changed ? CHANGED_BG : "transparent",
        verticalAlign: "middle",
        width: COL_WIDTH_EDITING,
      }}
    >
      {editingControl}
    </TableCell>
  </TableRow>
);

export default function EditTest() {
  const { userRole, currentUser } = useAuth();
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentRevision, setCurrentRevision] = useState(null);
  // The parent test doc — separate from the snapshot inside the revision.
  // Holds parent-only fields like `correctedImageUrl` (written by the
  // watermark Cloud Function after each save) that aren't part of the
  // immutable per-revision snapshot.
  const [parentDoc, setParentDoc] = useState(null);

  const [primaryGuideline, setPrimaryGuideline] = useState("EUCAST");
  const [fallbackToHuman, setFallbackToHuman] = useState(true);

  const [allAntibiotics, setAllAntibiotics] = useState([]);
  const [allOrganisms, setAllOrganisms] = useState([]);
  const [allSpecies, setAllSpecies] = useState([]);
  const [allBreakpoints, setAllBreakpoints] = useState([]);
  // B1 fix: org-specific custom breakpoints, used by the cascade so that
  // sites that only exist via a custom rule are reachable from the
  // dropdown. See interpretation_algorithm.md Section 7.2.
  const [allCustomBreakpoints, setAllCustomBreakpoints] = useState([]);
  const [allSpecimenTypes, setAllSpecimenTypes] = useState([]);
  // clinical_indication_master raw array. The lookup map is derived from
  // this via useMemo so it rebuilds on language change without refetching.
  const [allClinicalIndications, setAllClinicalIndications] = useState([]);

  // Correction-request integration — Path 1.
  const [correctionRequest, setCorrectionRequest] = useState(null);
  const [requesterName, setRequesterName] = useState("");

  // Editing state
  const [edited, setEdited] = useState(null);

  // Reason-for-edit (mandatory audit trail). Pre-filled with a hint
  // that references the correction-request reason if Path 1.
  const [editReason, setEditReason] = useState("");

  // Preview/save modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newInterpretations, setNewInterpretations] = useState(null);

  // Snackbar — replaces the legacy alert() for save success
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // ---- Initial data load ----
  useEffect(() => {
    if (!testId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const revision = await getCurrentRevision(testId);
        if (cancelled) return;
        if (!revision) {
          setError(
            "This test has no revisions. It may have been submitted before " +
              "the Phase 3 versioning model was introduced. Please contact " +
              "the system administrator.",
          );
          setLoading(false);
          return;
        }
        setCurrentRevision(revision);

        const orgId = revision.snapshot?.organizationId;
        if (!orgId) {
          setError("Test record is missing organizationId. Cannot proceed.");
          setLoading(false);
          return;
        }

        const [
          orgDetails,
          openRequest,
          specimenTypes,
          clinicalIndications,
          antibiotics,
          organisms,
          species,
          breakpoints,
          customBreakpoints,
          parent,
        ] = await Promise.all([
          getOrganizationDetails(orgId),
          getOpenRequestForTest(testId, orgId),
          getSpecimenTypes(),
          getClinicalIndications(),
          getAmrieAntibiotics(),
          getAmrieOrganisms(),
          getSpeciesMaster(),
          getAmrieBreakpoints(),
          getCustomBreakpoints(orgId),
          getTestResultById(testId),
        ]);
        if (cancelled) return;

        setPrimaryGuideline(orgDetails?.primaryGuideline || "EUCAST");
        setFallbackToHuman(orgDetails?.fallbackToHuman !== false);
        setCorrectionRequest(openRequest || null);
        setParentDoc(parent || null);

        // If Path 1 (open correction request), fetch the requester's name
        // for the alert at top of the form.
        if (openRequest?.requestedByUid) {
          try {
            const requesterProfile = await getUserProfile(
              openRequest.requestedByUid,
            );
            if (!cancelled) {
              setRequesterName(requesterProfile?.displayName || "Unknown user");
            }
          } catch (e) {
            console.warn("Failed to fetch requester profile:", e);
            if (!cancelled) setRequesterName("Unknown user");
          }
        }

        setAllSpecimenTypes(specimenTypes || []);
        setAllClinicalIndications(clinicalIndications || []);

        setAllAntibiotics(antibiotics || []);
        setAllOrganisms(organisms || []);
        setAllSpecies(species || []);
        setAllBreakpoints(breakpoints || []);
        setAllCustomBreakpoints(customBreakpoints || []);

        // Initialize editing state from the snapshot. Deep-copy disks.
        const snap = revision.snapshot || {};
        setEdited({
          caseId: snap.caseId || "",
          clientPatientName: snap.clientPatientName || "",
          specimenType: snap.specimenType || "",
          species: snap.species || "",
          organism: snap.organism || "",
          organismMo: snap.organismMo || null,
          clinicalIndication:
            snap.clinicalIndication || "General / Not specified",
          clinicalIndicationCode: snap.clinicalIndicationCode || null,
          notes: snap.notes || "",
          diskAssessments: (snap.diskAssessments || []).map((d) => ({ ...d })),
        });

        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("EditTest load error:", err);
        setError(err.message || "Failed to load test data.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [testId]);

  const snapshot = useMemo(
    () => currentRevision?.snapshot || null,
    [currentRevision],
  );

  // ---- Cascade derivations ----

  // Localized species lookup by stored English value. snapshot.species and
  // edited.species are stored as English display names (per the data layer's
  // English-only storage policy); this helper translates them for display.
  // Falls back to the original English value when a translation isn't available.
  const localizedSpeciesName = useCallback(
    (englishName) => {
      if (!englishName) return "";
      const sp = allSpecies.find(
        (s) => s.displayName === englishName || s.code === englishName,
      );
      if (!sp) return englishName;
      return sp.translations?.[i18n.language] || sp.displayName || englishName;
    },
    [allSpecies, i18n.language],
  );

  const selectedOrganism = useMemo(() => {
    if (!edited?.organism) return null;
    return allOrganisms.find((o) => o.organism === edited.organism) || null;
  }, [edited?.organism, allOrganisms]);

  const selectedSpecies = useMemo(() => {
    if (!edited?.species) return null;
    return (
      allSpecies.find(
        (s) => s.displayName === edited.species || s.code === edited.species,
      ) || null
    );
  }, [edited?.species, allSpecies]);

  const indicationOptions = useMemo(
    () =>
      filterClinicalIndications({
        organism: selectedOrganism,
        species: selectedSpecies,
        allBreakpoints,
        allCustomBreakpoints,
      }),
    [selectedOrganism, selectedSpecies, allBreakpoints, allCustomBreakpoints],
  );

  const sortedSpecimenTypes = useMemo(
    () =>
      [...allSpecimenTypes].sort((a, b) =>
        (a.displayName || "").localeCompare(b.displayName || ""),
      ),
    [allSpecimenTypes],
  );

  // Derived localization maps. Rebuilt on language change without refetching.
  // specimenTypeMap is keyed by code (the persisted form); clinicalIndicationMap
  // is keyed by display_name (which is what's stored on the test record).
  const specimenTypeMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const st of allSpecimenTypes) {
      if (st?.code) {
        m[st.code] = st.translations?.[lang] || st.displayName || st.code;
      }
    }
    return m;
  }, [allSpecimenTypes, i18n.language]);

  const clinicalIndicationMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const ci of allClinicalIndications) {
      if (ci?.displayName) {
        m[ci.displayName] = ci.translations?.[lang] || ci.displayName;
      }
    }
    return m;
  }, [allClinicalIndications, i18n.language]);

  // ---- Diff helpers ----
  const diff = useMemo(() => {
    if (!edited || !snapshot) return {};
    const changedDisks = (edited.diskAssessments || []).some((d, i) => {
      const o = snapshot.diskAssessments?.[i] || {};
      return (
        (d.antibioticId || d.ab) !== (o.antibioticId || o.ab) ||
        (d.potency || "") !== (o.potency || "") ||
        Number(d.diameterMm ?? -1) !== Number(o.diameterMm ?? -1) ||
        (d.mode || "M") !== (o.mode || "M")
      );
    });
    const out = {
      caseId: edited.caseId !== (snapshot.caseId || ""),
      clientPatientName:
        edited.clientPatientName !== (snapshot.clientPatientName || ""),
      specimenType: edited.specimenType !== (snapshot.specimenType || ""),
      species: edited.species !== (snapshot.species || ""),
      organism: edited.organism !== (snapshot.organism || ""),
      clinicalIndication:
        edited.clinicalIndication !==
        (snapshot.clinicalIndication || "General / Not specified"),
      notes: edited.notes !== (snapshot.notes || ""),
      diskAssessments: changedDisks,
    };
    out.anyChange = Object.values(out).some((v) => v === true);
    return out;
  }, [edited, snapshot]);

  // ---- Validation: all required fields filled? ----
  const validation = useMemo(() => {
    if (!edited) return { valid: false, missing: [] };
    const missing = [];
    if (!edited.caseId.trim()) missing.push(t("editTest.fieldCaseId"));
    if (!edited.organism.trim()) missing.push(t("editTest.organism"));
    if (!edited.species.trim()) missing.push(t("editTest.fieldHostSpecies"));
    if (!edited.clinicalIndication.trim())
      missing.push(t("editTest.clinicalIndication"));
    if (!edited.specimenType.trim()) missing.push(t("editTest.specimenType"));
    const incompleteDisks = (edited.diskAssessments || [])
      .map((d, i) => (diskRowComplete(d) ? null : i + 1))
      .filter((x) => x !== null);
    if (incompleteDisks.length > 0) {
      missing.push(
        t("editTest.tooltipDiskList", { list: incompleteDisks.join(", ") }),
      );
    }
    if (!editReason.trim()) missing.push(t("editTest.reasonForThisEdit"));
    return { valid: missing.length === 0, missing };
  }, [edited, editReason, t]);

  // ---- Form field handlers ----
  const updateField = useCallback((field, value) => {
    setEdited((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  /**
   * Organism change — cascade reset of host species AND clinical indication.
   * The editor must re-pick both downstream values.
   */
  const handleOrganismChange = useCallback((newOrg) => {
    setEdited((prev) => {
      if (!prev) return prev;
      if (!newOrg) {
        return {
          ...prev,
          organism: "",
          organismMo: null,
          species: "",
          clinicalIndication: "",
          clinicalIndicationCode: null,
        };
      }
      return {
        ...prev,
        organism: newOrg.organism,
        organismMo: newOrg.whonetOrgCode,
        species: "",
        clinicalIndication: "",
        clinicalIndicationCode: null,
      };
    });
  }, []);

  /**
   * Host species change — cascade reset of clinical indication.
   */
  const handleSpeciesChange = useCallback((newSpecies) => {
    setEdited((prev) => {
      if (!prev) return prev;
      if (!newSpecies) {
        return {
          ...prev,
          species: "",
          clinicalIndication: "",
          clinicalIndicationCode: null,
        };
      }
      return {
        ...prev,
        species: newSpecies.displayName,
        clinicalIndication: "",
        clinicalIndicationCode: null,
      };
    });
  }, []);

  const handleIndicationChange = useCallback((option) => {
    setEdited((prev) =>
      prev
        ? {
            ...prev,
            clinicalIndication: option?.display || "",
            clinicalIndicationCode: option?.code || null,
          }
        : prev,
    );
  }, []);

  const handleSpecimenTypeChange = useCallback((newSt) => {
    setEdited((prev) =>
      prev ? { ...prev, specimenType: newSt?.code || "" } : prev,
    );
  }, []);

  const handleDiskChange = useCallback((index, updatedDisk) => {
    setEdited((prev) => {
      if (!prev) return prev;
      const next = [...(prev.diskAssessments || [])];
      next[index] = updatedDisk;
      return { ...prev, diskAssessments: next };
    });
  }, []);

  // ---- Preview / Save handlers ----

  /**
   * Click "Preview changes" — calls the Cloud Function to compute new
   * interpretations for the edited data, then opens the preview dialog.
   * Editor sees what the new interpretations look like alongside the
   * editReason before committing.
   */
  const handlePreview = useCallback(async () => {
    if (!validation.valid || !edited || !selectedSpecies) return;
    setSaving(true);
    setSaveError(null);
    setNewInterpretations(null);

    try {
      // Build the Cloud Function request payload. Note: only passing the
      // first host from amrieHostsToTry — multi-host species (Horses)
      // need a Cloud Function extension, on revisit list.
      const hosts = amrieHostsToTry(selectedSpecies);
      const host = hosts.length > 0 ? hosts[0] : "Human";
      const track = selectedSpecies.track === "human" ? "human" : "vet";

      const payload = {
        organizationId: snapshot.organizationId,
        organismCode: edited.organismMo,
        track,
        host,
        siteOfInfection: edited.clinicalIndicationCode,
        year: 2026,
        fallbackToHuman,
        diskAssessments: edited.diskAssessments.map((d) => ({
          antibioticCode: d.antibioticId || d.ab,
          antibioticId: d.antibioticId || d.ab,
          antibioticName: d.antibioticName,
          antibioticSymbol: d.antibioticSymbol,
          potency: d.potency,
          measurementMm: d.diameterMm,
          mode: d.mode || "M",
        })),
      };

      const fns = getFunctions(app, "us-central1");
      const callable = httpsCallable(fns, "interpret_measurements");
      const result = await callable(payload);
      setNewInterpretations(result.data);
      setPreviewOpen(true);
    } catch (err) {
      console.error("interpret_measurements failed:", err);
      setSaveError(
        `Failed to compute new interpretations: ${err?.message || err}`,
      );
    } finally {
      setSaving(false);
    }
  }, [validation.valid, edited, selectedSpecies, snapshot, fallbackToHuman]);

  /**
   * Click "Save revision" inside the preview dialog — commits the new
   * revision, resolves the correction request if any, and navigates per
   * role.
   */
  const handleSave = useCallback(async () => {
    if (!newInterpretations || !edited) return;
    setSaving(true);
    setSaveError(null);

    try {
      // Build the new snapshot: the current edited fields PLUS the freshly
      // computed interpretations baked onto each disk. Same shape as
      // Android writes for new submissions (DataRepository.kt).
      const computedDisks = newInterpretations.diskAssessments || [];
      const newSnapshot = {
        // Preserve fields that aren't editor-controlled
        submitterUid: snapshot.submitterUid,
        organizationId: snapshot.organizationId,
        finalImageUrl: snapshot.finalImageUrl,
        // Editor-controlled fields
        caseId: edited.caseId,
        clientPatientName: edited.clientPatientName,
        specimenType: edited.specimenType,
        species: edited.species,
        organism: edited.organism,
        organismMo: edited.organismMo,
        clinicalIndication: edited.clinicalIndication,
        clinicalIndicationCode: edited.clinicalIndicationCode,
        notes: edited.notes,
        // Re-attach the freshly computed interpretations to each disk.
        // Match by index — the order is preserved from the editor.
        diskAssessments: edited.diskAssessments.map((d, i) => ({
          antibioticId: d.antibioticId || d.ab,
          antibioticSymbol: d.antibioticSymbol,
          antibioticName: d.antibioticName,
          ab: d.antibioticId || d.ab,
          mode: d.mode || "M",
          diameterMm: d.diameterMm,
          potency: d.potency,
          interpretations: computedDisks[i]?.interpretations || [],
        })),
      };

      const result = await addRevision({
        testId,
        newSnapshot,
        editorUid: currentUser.uid,
        changeReason: editReason,
      });

      // Resolve the correction request if there was one (Path 1).
      let updatedRequestedTestIds = location.state?.requestedTestIds;
      if (correctionRequest) {
        try {
          await resolveCorrectionRequest(correctionRequest.id);
          if (updatedRequestedTestIds) {
            updatedRequestedTestIds = updatedRequestedTestIds.filter(
              (id) => id !== testId,
            );
          }
        } catch (e) {
          // Don't fail the whole save if request resolution fails — the
          // revision is already written. Admin can manually resolve later.
          console.warn("Failed to resolve correction request:", e);
        }
      }

      setSnackbar({
        open: true,
        message: t("editTest.revisionSavedSuccess", {
          number: result.revisionNumber,
        }),
      });
      setPreviewOpen(false);

      // Navigate after a short delay so the snackbar is visible.
      setTimeout(() => {
        if (userRole === "pro_user") {
          navigate("/app/find-test");
        } else if (
          updatedRequestedTestIds &&
          updatedRequestedTestIds.length > 0
        ) {
          navigate("/app/corrections-queue", {
            state: { requestedTestIds: updatedRequestedTestIds },
            replace: true,
          });
        } else {
          navigate("/app/dashboard");
        }
      }, 800);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError(`Save failed: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  }, [
    newInterpretations,
    edited,
    snapshot,
    testId,
    currentUser,
    editReason,
    location.state,
    correctionRequest,
    userRole,
    navigate,
    t,
  ]);

  // ---- Render ----
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t("editTest.loadingData")}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          {t("common.back") || "Back"}
        </Button>
      </Container>
    );
  }

  if (!snapshot || !edited) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">{t("editTest.noDataLoaded")}</Alert>
      </Container>
    );
  }

  const revisionLabel = t("editTest.revisionLabel", {
    number: currentRevision.revisionNumber,
  });
  const editedFlag = (currentRevision.revisionNumber || 1) > 1;
  // Image source: prefer the parent doc's correctedImageUrl (latest
  // watermarked file written by the watermark Cloud Function), fall back
  // to the original. correctedImageUrl is parent-doc state, NOT in the
  // snapshot, because it's set after addRevision returns when the
  // watermark trigger fires.
  // Race note: if the editor opens EditTest within ~10-30s of the previous
  // save, the watermark function may not have finished and the URL still
  // points at the previous revision's image. Refresh resolves it.
  const imageUrl =
    parentDoc?.correctedImageUrl ||
    snapshot.correctedImageUrl ||
    snapshot.finalImageUrl;
  const imageIsWatermarked = !!parentDoc?.correctedImageUrl;

  // Disable Preview unless something changed AND everything required is filled.
  const previewDisabled = !diff.anyChange || !validation.valid;
  const previewTooltip = !diff.anyChange
    ? t("editTest.previewTooltipNoChanges")
    : !validation.valid
      ? t("editTest.previewTooltipMissingFields", {
          fields: validation.missing.join("; "),
        })
      : "";

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          {t("editTest.editCaseHeading", { caseId: snapshot.caseId || testId })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("editTest.editingFrom", { revisionLabel })}
          {editedFlag && ` ${t("editTest.editedPreviously")}`}
          {diff.anyChange && (
            <Box
              component="span"
              sx={{ ml: 2, color: "warning.main", fontWeight: "bold" }}
            >
              {t("editTest.unsavedChanges")}
            </Box>
          )}
        </Typography>
      </Box>

      {/* Path-1 correction request alert. Path 2 (direct admin edit)
          renders nothing here. */}
      {correctionRequest && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t("editTest.correctionRequested")}</AlertTitle>
          <Typography variant="body2">
            {t("editTest.correctionFromAt", {
              name: requesterName,
              time: correctionRequest.requestedAt
                ? new Date(
                    correctionRequest.requestedAt.seconds * 1000,
                  ).toLocaleString()
                : t("editTest.unknownTime"),
            })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>{t("editTest.reasonLabel")}</strong>{" "}
            {correctionRequest.reason}
          </Typography>
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 0 }}>
        {/* The aligned-row metadata table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.200" }}>
                <TableCell sx={{ fontWeight: "bold", width: COL_WIDTH_FIELD }}>
                  {t("editTest.tableField")}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", width: COL_WIDTH_CURRENT }}
                >
                  {t("editTest.tableCurrent")}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", width: COL_WIDTH_EDITING }}
                >
                  {t("editTest.tableEditing")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Case ID */}
              <FieldRow
                label={t("editTest.fieldCaseId")}
                changed={diff.caseId}
                currentDisplay={snapshot.caseId}
                editingControl={
                  <TextField
                    fullWidth
                    size="small"
                    value={edited.caseId}
                    onChange={(e) => updateField("caseId", e.target.value)}
                    error={!edited.caseId.trim()}
                  />
                }
              />

              {/* Organism */}
              <FieldRow
                label={t("editTest.organism")}
                changed={diff.organism}
                currentDisplay={snapshot.organism}
                editingControl={
                  <Autocomplete
                    size="small"
                    options={allOrganisms}
                    getOptionLabel={(opt) => opt.organism || ""}
                    value={selectedOrganism}
                    onChange={(_e, v) => handleOrganismChange(v)}
                    isOptionEqualToValue={(opt, val) =>
                      opt.whonetOrgCode === val?.whonetOrgCode
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("editTest.placeholderOrganism")}
                        error={!edited.organism.trim()}
                      />
                    )}
                  />
                }
              />

              {/* Host species */}
              <FieldRow
                label={t("editTest.fieldHostSpecies")}
                changed={diff.species}
                currentDisplay={localizedSpeciesName(snapshot.species)}
                editingControl={
                  <Autocomplete
                    size="small"
                    options={allSpecies}
                    getOptionLabel={(opt) =>
                      opt.translations?.[i18n.language] || opt.displayName || ""
                    }
                    value={selectedSpecies}
                    onChange={(_e, v) => handleSpeciesChange(v)}
                    isOptionEqualToValue={(opt, val) => opt.code === val?.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("editTest.placeholderHostSpecies")}
                        error={!edited.species.trim()}
                      />
                    )}
                  />
                }
              />

              {/* Clinical indication */}
              <FieldRow
                label={t("editTest.clinicalIndication")}
                changed={diff.clinicalIndication}
                currentDisplay={
                  snapshot.clinicalIndication
                    ? clinicalIndicationMap[snapshot.clinicalIndication] ||
                      snapshot.clinicalIndication
                    : ""
                }
                editingControl={
                  <FormControl
                    fullWidth
                    size="small"
                    error={!edited.clinicalIndication.trim()}
                  >
                    <Select
                      displayEmpty
                      value={edited.clinicalIndication}
                      onChange={(e) => {
                        const opt = indicationOptions.find(
                          (o) => o.display === e.target.value,
                        );
                        handleIndicationChange(opt);
                      }}
                      // Localize the rendered selected value while keeping
                      // the underlying value as the English display string
                      // (the lookup key for breakpoint matching).
                      renderValue={(value) => {
                        if (!value) return "";
                        return clinicalIndicationMap[value] || value;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>{t("editTest.selectIndication")}</em>
                      </MenuItem>
                      {indicationOptions.map((opt) => (
                        <MenuItem
                          key={opt.code || "general"}
                          value={opt.display}
                        >
                          {clinicalIndicationMap[opt.display] || opt.display}
                        </MenuItem>
                      ))}
                    </Select>
                    {/* Help text: explain "General / Not specified" semantics
                        so users understand when to pick a specific site
                        instead. See interpretation_algorithm.md Section 11.2. */}
                    {edited.clinicalIndication ===
                      "General / Not specified" && (
                      <FormHelperText>
                        {t("editTest.breakpointHelp")}
                      </FormHelperText>
                    )}
                  </FormControl>
                }
              />

              {/* Specimen type */}
              <FieldRow
                label={t("editTest.specimenType")}
                changed={diff.specimenType}
                currentDisplay={
                  snapshot.specimenType
                    ? specimenTypeMap[snapshot.specimenType] ||
                      snapshot.specimenType
                    : ""
                }
                editingControl={
                  <Autocomplete
                    size="small"
                    options={sortedSpecimenTypes}
                    getOptionLabel={(opt) =>
                      opt.translations?.[i18n.language] || opt.displayName || ""
                    }
                    value={
                      sortedSpecimenTypes.find(
                        (s) => s.code === edited.specimenType,
                      ) || null
                    }
                    onChange={(_e, v) => handleSpecimenTypeChange(v)}
                    isOptionEqualToValue={(opt, val) => opt.code === val?.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("editTest.placeholderSpecimenType")}
                        error={!edited.specimenType.trim()}
                      />
                    )}
                  />
                }
              />

              {/* Notes */}
              <FieldRow
                label={t("editTest.notes")}
                changed={diff.notes}
                currentDisplay={snapshot.notes}
                editingControl={
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={edited.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                  />
                }
              />
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        {/* Disk assessments — paired-row editor */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("editTest.diskAssessments")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            {t("editTest.primaryGuidelineForOrg", {
              guideline: primaryGuideline,
            })}
          </Typography>
          <DiskAssessmentEditor
            disks={edited.diskAssessments}
            originalDisks={snapshot.diskAssessments}
            allAntibiotics={allAntibiotics}
            allBreakpoints={allBreakpoints}
            onChange={handleDiskChange}
          />
        </Box>

        <Divider />

        {/* Plate image — latest watermarked image (or original for rev 1) */}
        {imageUrl && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("editTest.plateImage")}
              {imageIsWatermarked && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: "0.85rem",
                    color: "text.secondary",
                    fontWeight: "normal",
                  }}
                >
                  {t("editTest.watermarkedFromRev", {
                    number: currentRevision.revisionNumber,
                  })}
                </Box>
              )}
            </Typography>
            <Box
              component="img"
              src={imageUrl}
              alt={t("editTest.annotatedPlateAlt")}
              sx={{
                maxWidth: "100%",
                maxHeight: 600,
                display: "block",
                margin: "0 auto",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              {imageIsWatermarked
                ? t("editTest.imageCaptionWatermarked")
                : t("editTest.imageCaptionOriginal")}
            </Typography>
          </Box>
        )}

        <Divider />

        {/* Reason-for-edit (mandatory) — audit trail, persisted as
            changeReason on the new revision. */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("editTest.reasonForThisEdit")}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder={
              correctionRequest
                ? t("editTest.reasonPlaceholderCorrection")
                : t("editTest.reasonPlaceholderDefault")
            }
            error={!editReason.trim()}
            helperText={
              !editReason.trim()
                ? t("editTest.reasonHelperRequired")
                : t("editTest.reasonHelperCharCount", {
                    count: editReason.length,
                  })
            }
          />
        </Box>

        <Divider />

        {/* Action bar */}
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Tooltip title={previewTooltip} arrow placement="top">
            <span>
              <Button
                variant="contained"
                disabled={previewDisabled || saving}
                onClick={handlePreview}
              >
                {saving ? (
                  <>
                    <CircularProgress
                      size={18}
                      sx={{ mr: 1, color: "inherit" }}
                    />
                    {t("editTest.computing")}
                  </>
                ) : (
                  t("editTest.previewChanges")
                )}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {saveError && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Alert severity="error">{saveError}</Alert>
          </Box>
        )}
      </Paper>

      {/* Preview / save dialog */}
      <PreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        edited={edited}
        snapshot={snapshot}
        diff={diff}
        editReason={editReason}
        primaryGuideline={primaryGuideline}
        newInterpretations={newInterpretations}
        saving={saving}
        saveError={saveError}
        onSave={handleSave}
        specimenTypeMap={specimenTypeMap}
        clinicalIndicationMap={clinicalIndicationMap}
        allSpecies={allSpecies}
      />

      {/* Snackbar for save success */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbar({ open: false, message: "" })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

// =====================================================================
// PreviewDialog — modal shown after Preview is clicked.
//
// Displays:
//   1. A field-by-field diff (only fields that changed).
//   2. The disk-by-disk diff (each changed disk shown current → new).
//   3. The new multi-source interpretations as they would be saved.
//   4. The reason text (read-only — displayed as confirmation, not edit).
//   5. Save / Cancel buttons. Save commits via the parent's onSave.
// =====================================================================

function PreviewDialog({
  open,
  onClose,
  edited,
  snapshot,
  diff,
  editReason,
  primaryGuideline,
  newInterpretations,
  saving,
  saveError,
  onSave,
  specimenTypeMap,
  clinicalIndicationMap,
  allSpecies,
}) {
  const { t, i18n } = useTranslation();

  if (!open || !edited || !snapshot) return null;

  // Localize host-species labels in the diff display. snapshot.species and
  // edited.species are stored as English display names; this lookup matches
  // them against species_master and renders the localized label.
  const localizeSpecies = (name) => {
    if (!name) return name;
    const sp = (allSpecies || []).find(
      (s) => s.displayName === name || s.code === name,
    );
    if (!sp) return name;
    return sp.translations?.[i18n.language] || sp.displayName || name;
  };

  // Build the rows for the field-by-field diff. Each row: label, old, new.
  const fieldRows = [];
  if (diff.caseId) {
    fieldRows.push([t("editTest.fieldCaseId"), snapshot.caseId, edited.caseId]);
  }
  if (diff.organism) {
    fieldRows.push([
      t("editTest.organism"),
      snapshot.organism,
      edited.organism,
    ]);
  }
  if (diff.species) {
    fieldRows.push([
      t("editTest.fieldHostSpecies"),
      localizeSpecies(snapshot.species),
      localizeSpecies(edited.species),
    ]);
  }
  if (diff.clinicalIndication) {
    fieldRows.push([
      t("editTest.clinicalIndication"),
      (clinicalIndicationMap || {})[snapshot.clinicalIndication] ||
        snapshot.clinicalIndication,
      (clinicalIndicationMap || {})[edited.clinicalIndication] ||
        edited.clinicalIndication,
    ]);
  }
  if (diff.specimenType) {
    fieldRows.push([
      t("editTest.specimenType"),
      specimenTypeMap[snapshot.specimenType] || snapshot.specimenType,
      specimenTypeMap[edited.specimenType] || edited.specimenType,
    ]);
  }
  if (diff.clientPatientName) {
    fieldRows.push([
      t("editTest.fieldClientPatient"),
      snapshot.clientPatientName,
      edited.clientPatientName,
    ]);
  }
  if (diff.notes) {
    fieldRows.push([t("editTest.notes"), snapshot.notes, edited.notes]);
  }

  // Collect disk diffs: rows where any of antibiotic, potency, zone, mode
  // changed, plus the new interpretations for that disk.
  const diskDiffRows = (edited.diskAssessments || []).map((d, i) => {
    const o = snapshot.diskAssessments?.[i] || {};
    const abChanged = (d.antibioticId || d.ab) !== (o.antibioticId || o.ab);
    const potencyChanged = (d.potency || "") !== (o.potency || "");
    const zoneChanged =
      Number(d.diameterMm ?? -1) !== Number(o.diameterMm ?? -1);
    const modeChanged = (d.mode || "M") !== (o.mode || "M");
    const anyChange = abChanged || potencyChanged || zoneChanged || modeChanged;
    const newDisk = newInterpretations?.diskAssessments?.[i];
    // Compute primary-source interpretation summary for compact display
    const newInterp = newDisk?.interpretations?.find(
      (x) => x.source === primaryGuideline,
    );
    const oldInterp = o.interpretations?.find(
      (x) => x.source === primaryGuideline,
    );
    return {
      index: i,
      anyChange,
      oldDrug: o.antibioticName || o.ab || "—",
      newDrug: d.antibioticName || d.ab || "—",
      oldPotency: o.potency || "—",
      newPotency: d.potency || "—",
      oldZone:
        o.mode === "NZ"
          ? "(NZ)"
          : o.diameterMm != null
            ? `${o.diameterMm} mm`
            : "—",
      newZone:
        d.mode === "NZ"
          ? "(NZ)"
          : d.diameterMm != null
            ? `${d.diameterMm} mm`
            : "—",
      oldSir: oldInterp?.interpretation || "—",
      newSir: newInterp?.interpretation || "—",
      sirChanged:
        (oldInterp?.interpretation || null) !==
        (newInterp?.interpretation || null),
    };
  });

  const changedDisks = diskDiffRows.filter((r) => r.anyChange);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("editTest.previewRevision")}</DialogTitle>
      <DialogContent dividers>
        {fieldRows.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("editTest.metadataChanges")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    {t("editTest.tableField")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                    {t("editTest.tableFrom")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                    {t("editTest.tableTo")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fieldRows.map(([label, from, to]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ fontWeight: "bold" }}>{label}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {from || "—"}
                    </TableCell>
                    <TableCell>{to || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {changedDisks.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("editTest.diskChangesAndInterpretations", {
                guideline: primaryGuideline,
              })}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("editTest.tableWas")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("editTest.tableNow")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("editTest.tableSirChange")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changedDisks.map((r) => (
                  <TableRow key={r.index}>
                    <TableCell>{r.index + 1}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {r.oldDrug} {r.oldPotency} · {r.oldZone}
                    </TableCell>
                    <TableCell>
                      {r.newDrug} {r.newPotency} · {r.newZone}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: r.sirChanged ? "warning.main" : "text.secondary",
                        fontWeight: r.sirChanged ? "bold" : "normal",
                      }}
                    >
                      {r.oldSir} → {r.newSir}
                      {r.sirChanged && ` ${t("editTest.changedSuffix")}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {t("editTest.guidelineColumnNote", {
                guideline: primaryGuideline,
              })}
            </Typography>
          </Box>
        )}

        {fieldRows.length === 0 && changedDisks.length === 0 && (
          <Alert severity="info">{t("editTest.noChangesDetected")}</Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("editTest.reasonForThisEdit")}
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 1.5, bgcolor: "grey.50", whiteSpace: "pre-wrap" }}
          >
            {editReason || t("editTest.noneProvided")}
          </Paper>
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("editTest.previewBack")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? t("editTest.saving") : t("editTest.saveRevision")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
